import {
    assert,
    copyObjectWithoutKeys,
    getNonEmptyArrayLastElement,
    isArrayNotEmpty,
    nameFunction,
    objectEntries,
    objectKeys,
    type ReadonlyNonEmptyArray,
} from '../../utils'

import type { LastTopicMessage, Topic, TopicSubscriptionError } from '..'
import { ConnectwareError, ConnectwareErrorType } from '../Error'
import type { Explorer, LoadableTopicsChunk, TopicsNextChunkMetadata, TopicTree } from '.'
import { isLoadableLoaded, type Loadable } from '../Loadable'

export type ExplorerAppState = Readonly<{ explorer: Loadable<Explorer> }>

export const selectExplorer = (s: ExplorerAppState): ExplorerAppState['explorer'] => s.explorer

export const selectLoadedExplorer = (s: ExplorerAppState): Explorer => {
    const explorer = selectExplorer(s)
    assert(explorer && !ConnectwareError.is(explorer), new ConnectwareError(ConnectwareErrorType.STATE, 'Can not retrieve explorer'))
    return explorer
}

export const selectExplorerError = (s: ExplorerAppState): ConnectwareError | null => {
    const explorer = selectExplorer(s)
    return ConnectwareError.is(explorer) ? explorer : null
}

const createInitializedExplorerSelector = <Result, Args extends unknown[] = never[]>(
    selector: (explorer: Explorer, args: Args, defaultValue: Result) => Result,
    defaultValue: Result
): (s: ExplorerAppState, ...args: Args) => Result =>
(s, ...args) => {
    const explorer: ExplorerAppState['explorer'] = selectExplorer(s)
    return !explorer || ConnectwareError.is(explorer) ? defaultValue : selector(explorer, args, defaultValue)
}

export const selectExplorerChunks = createInitializedExplorerSelector((e) => e.chunks, new Map<never, never>())

export const selectExplorerLoadedChunkPrefixes = createInitializedExplorerSelector<readonly TopicTree['fullPath'][]>((e) => Array.from(e.chunks.keys()), [])

export const selectExplorerCustomTopics = createInitializedExplorerSelector<readonly string[]>((e) => e.customTopics, [])

export const selectExplorerSelectedTopics = createInitializedExplorerSelector<readonly string[]>((e) => e.selectedTopics, [])

export const selectExplorerAreTopicAvailable = nameFunction(
    createInitializedExplorerSelector(
        ({ selectedTopics, customTopics, chunks }) =>
            isArrayNotEmpty(selectedTopics) ||
            isArrayNotEmpty(customTopics) ||
            Array.from(chunks).some(([, prefixChunks]) => prefixChunks.some((chunk) => !isLoadableLoaded(chunk) || isArrayNotEmpty(chunk.topics))),
        false
    ),
    'selectExplorerAreTopicAvailable'
)

export const selectExplorerDoesTopicExist = createInitializedExplorerSelector<boolean, [topics: string]>(
    (e, [topic]) =>
        e.customTopics.includes(topic) ||
        e.selectedTopics.includes(topic) ||
        Array.from(e.chunks).some(([, prefixChunks]) => prefixChunks.some((c) => isLoadableLoaded(c) && c.topics.some((t) => t.fullPath === topic))),
    false
)

export const selectExplorerSubscriptionResponseErrors = createInitializedExplorerSelector<readonly TopicSubscriptionError[], [path: Topic<false>['fullPath']]>(
    ({ subscriptionResponse }, [fullPath], defaultValue) =>
        subscriptionResponse === null
            ? defaultValue
            : objectEntries(copyObjectWithoutKeys(subscriptionResponse, 'subscribed')).reduce<readonly TopicSubscriptionError[]>(
                (r, [errorType, topics]) => (topics.includes(fullPath) ? [...r, errorType] : r),
                defaultValue
            ),
    []
)

export const selectExplorerMonitoredTopics = createInitializedExplorerSelector<readonly string[]>(({ latestValues }) => objectKeys(latestValues).sort(), [])

export const selectExplorerMonitoredTopicsMessage = createInitializedExplorerSelector<LastTopicMessage | null, [topic: string]>(
    ({ latestValues }, [topic], defaultValue) => latestValues[topic] ?? defaultValue,
    null
)

export const selectExplorerMonitoredTopicsToUnsubcribe = createInitializedExplorerSelector<readonly string[], [topic: string]>(
    ({ selectedTopics, latestValues }, [topic], defaultValue) => {
        const selected = new Set(selectedTopics)

        if (selected.has(topic)) {
            return [topic]
        }

        const sources = latestValues[topic]?.sources ?? defaultValue

        /**
         * Ensuring that the given sources are still subscribed to
         */
        return sources.filter((t) => selected.has(t))
    },
    []
)

export const selectExplorerLatestTopicValues = createInitializedExplorerSelector<Explorer['latestValues']>((e) => e.latestValues, {})

export const selectExplorerTailSize = createInitializedExplorerSelector((e) => e.tailSize, 0)

export const selectExplorerIsTailing = createInitializedExplorerSelector((e) => e.isTailing, null)

export const selectExplorerTail = createInitializedExplorerSelector((e) => e.tail, [])

export const selectExplorerLastTopicsChunkNextReference = (s: ExplorerAppState, prefix: TopicTree['fullPath']): TopicsNextChunkMetadata['reference'] | null => {
    const chunks = selectExplorerChunks(s).get(prefix)

    if (!chunks) {
        return null
    }

    const result = getNonEmptyArrayLastElement(chunks)
    assert(
        isLoadableLoaded(result) && result.nextChunk !== null,
        new ConnectwareError(ConnectwareErrorType.STATE, 'Cannot resolve to next chunk offset', { prefix, result })
    )
    return result.nextChunk.reference
}

export const selecExplorerTopicsChunk = createInitializedExplorerSelector<readonly LoadableTopicsChunk[], [prefix: TopicTree['fullPath']]>(
    ({ chunks }, [prefix], defaultValue) => chunks.get(prefix) ?? defaultValue,
    []
)

export const selecExplorerExistingTopicsChunkWithUnloaded = (
    s: ExplorerAppState,
    prefix: TopicTree['fullPath'],
    position: number
): ReadonlyNonEmptyArray<LoadableTopicsChunk> => {
    const chunk = selecExplorerTopicsChunk(s, prefix)
    assert(isArrayNotEmpty(chunk) && chunk[position] === null, new ConnectwareError(ConnectwareErrorType.STATE, 'Unexpected chunk state', { prefix, position }))
    return chunk
}
